import { useAtomValue } from "jotai";
import { useMemo } from "react";

import { tenantIDAtom } from "../atoms/GlobalAtoms";
import useWorkspace from "../api/api-hooks/workspaces/useWorkspace";
import useGetCloudApps from "../api/api-hooks/cloudApps/useGetCloudApps";
import useInstallGlobalApp from "../api/api-hooks/globalApps/useInstallGlobalApp";
import useAddGlobalAppToWorkspace from "../api/api-hooks/globalApps/useAddGlobalAppToWorkspace";
import useUninstallGlobalApp from "../api/api-hooks/globalApps/useUninstallGlobalApp";
import { AppConfirm } from "../utils/PopupUtil";
import { GlobalApp } from "../types/GlobalInterfaces";

/**
 * Extends the provided app with additional information.
 * @param app Cloud/Global App
 */
const useGrowthCloudStoreApp = ({
  app,
  disabled,
}: {
  app: any;
  disabled: boolean;
}) => {
  const tenantID = useAtomValue(tenantIDAtom);
  const { workspace } = useWorkspace();
  const { cloudApps, loadingApps: loadingCloudApps } = useGetCloudApps(
    undefined,
    disabled
  );
  const { installGlobalApp, installID } = useInstallGlobalApp();
  const { addGlobalAppToWorkspace, addingApp } = useAddGlobalAppToWorkspace();
  const { uninstallGlobalApp, uninstallingApp } = useUninstallGlobalApp();

  function handleInstallApp(appID: string) {
    if (!workspace?.id) return;
    installGlobalApp({
      payload: {
        globalAppID: appID,
        workspaceID: workspace?.id,
      },
    });
  }

  function handleAddAppToWS(appID: string) {
    if (!workspace?.id) return;
    addGlobalAppToWorkspace({
      payload: {
        globalAppID: appID,
        workspaceID: workspace?.id,
      },
    });
  }

  async function handleUninstallApp(app: GlobalApp) {
    const result = await AppConfirm({
      title: `Uninstall ${app.Name}?`,
      html: "Are you sure you want to uninstall this app? <br/> It will be removed from ALL workspaces in this cloud!",
      confirmButtonText: "Uninstall",
    });

    if (!result.isConfirmed) return;

    const result2 = await AppConfirm({
      title: "Final Warning",
      text: "All data sources and records associated with this app will be lost. This action cannot be undone.",
      confirmButtonText: "Uninstall",
    });

    if (!result2.isConfirmed) return;

    if (!app._id) return;
    uninstallGlobalApp({
      globalAppID: app?._id,
      deleteEverything: true,
    });
  }

  // All of the cloud apps that are GLOBAL.
  const appsInCloud = cloudApps?.flatMap((cApp: any) => {
    return !!cApp?.GlobalAppID ? [cApp.GlobalAppID] : [];
  });

  // It's a global App
  const appsInWorkspace = useMemo(() => {
    return workspace?.Sidebar?.flatMap((item: any) => {
      return !!item?.GlobalAppID ? [item.GlobalAppID] : [];
    });
  }, [workspace, cloudApps]);

  // A map of the app IDs to their current app version in the SIDEBAR
  // We use this to check and see if updates are available.
  const appsVersionsSidebar = useMemo(() => {
    return workspace?.Sidebar?.reduce(
      (result: { [key: string]: string }, item: any) => {
        if (!item?.GlobalAppID) return result;

        result[item.GlobalAppID] = item.GlobalAppVersion || 0;

        return result;
      },
      {}
    );
  }, [workspace, cloudApps]);

  const appsVersions = useMemo(() => {
    return cloudApps?.reduce((result: { [key: string]: number }, cApp: any) => {
      if (!cApp?.GlobalAppID) return result;

      result[cApp.GlobalAppID] = cApp.GlobalAppVersion || 0;

      return result;
    }, {});
  }, [cloudApps]);

  const extendedApp = useMemo(() => {
    // It's a Cloud App.
    if (!app?.OriginCloudID) {
      return {
        ...app,
        Name: app.Title,
        isSameCloud: true,
        isInstalledOnWS: true,
        isInstalledOnCloud: true,
        isInstalled: true,
        isInstalledButNotInWS: false,
        isOutdatedSidebar: false,
        isOutdated: false,
        isCloudApp: true,
        buttonDisabled: true,
        buttonText: "Is Cloud App",
        visbility: true,
      };
    }

    const updatedApp = { ...app, Title: app.Name };
    const isSameCloud = app?.OriginCloudID === tenantID;
    const isInstalledOnWS = appsInWorkspace?.includes(app.id);
    const isInstalledOnCloud = appsInCloud?.includes(app.id);

    const isInstalled = isSameCloud || isInstalledOnWS;
    const isInstalledButNotInWS = isInstalledOnCloud && !isInstalledOnWS;

    const isOutdatedSidebar = isSameCloud
      ? false
      : isInstalled && app.Version !== appsVersionsSidebar[app.id];
    const isOutdated = isInstalled && app.Version !== appsVersions?.[app.id];

    let buttonText = isInstalled ? "Uninstall" : "Install";

    // App is installed on cloud, but not available in workspace.
    if (isInstalledButNotInWS) {
      buttonText = "Add To Workspace";
    }

    // App is outdated, and needs to be updated.
    if (isOutdated || isOutdatedSidebar) {
      buttonText = "Update";
    }

    if (isSameCloud) {
      buttonText = "Created on this cloud";
    }

    let installFunc = isInstalledButNotInWS
      ? () => handleAddAppToWS(app.id)
      : () => handleInstallApp(app.id);

    if (isOutdated) {
      installFunc = () => handleInstallApp(app.id);
    }

    if (!isOutdated && isOutdatedSidebar) {
      installFunc = () => handleAddAppToWS(app.id);
    }

    const uninstallFunc = () => handleUninstallApp(app);

    return {
      ...updatedApp,
      isSameCloud: isSameCloud,
      isInstalledOnWS: isInstalledOnWS,
      isInstalledOnCloud: isInstalledOnCloud,
      isInstalled: isInstalled,
      isInstalledButNotInWS: isInstalledButNotInWS,
      isOutdatedSidebar: isOutdatedSidebar,
      isOutdated: isOutdated,
      isCloudApp: false,
      buttonText: buttonText,
      visibility:
        workspace?.AppsVisibility?.[app.id] === undefined
          ? true
          : workspace?.AppsVisibility?.[app.id],
      uninstallFunc,
      installFunc,
    };
  }, [cloudApps, appsInWorkspace, appsInCloud, app]);

  return {
    extendedApp,
    installID,
    addingApp,
    uninstallingApp,
  };
};

export default useGrowthCloudStoreApp;
